import { useState, useEffect } from 'react'

// Mantine
import {
    Combobox,
    InputBase,
    useCombobox,
    Button,
    ActionIcon,
    Tooltip,
    Modal,
} from '@mantine/core'

// Icons
import { IconSettings } from '@tabler/icons-react'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

export default function PurchaseOrderSupplierSelect({
    onChange,
    value,
    error,
}) {
    // API
    const { requestGet, requestPost, requestPatch, requestDelete } =
        useAxiosApi()

    // Suppliers list
    const [suppliers, setSuppliers] = useState([])

    // Fetch suppliers
    const fetchSuppliers = async () => {
        const { data } = await requestGet('/suppliers')

        setSuppliers(data)
    }

    // Fetch suppliers on mount
    useEffect(() => {
        fetchSuppliers()
    }, [])

    // Edit search when value changes
    useEffect(() => {
        if (value) {
            setSearch(value.name)
        }
    }, [value])

    // Edition modal
    const [editingSupplier, setEditingSupplier] = useState(null)
    const [isModalOpened, setModalOpened] = useState(false)

    const [editingSupplierName, setEditingSupplierName] = useState('')

    const canEditingSupplierBeDeleted =
        editingSupplier?.purchase_orders_count === 0

    const openSupplierEditor = (supplier) => {
        setEditingSupplier(supplier)
        setEditingSupplierName(supplier.name)

        setDeletingSupplier(false)

        setModalOpened(true)
    }

    // Delete supplier
    const [isDeletingSupplier, setDeletingSupplier] = useState(false)

    const deleteSupplier = async () => {
        setDeletingSupplier(true)

        try {
            await requestDelete(`/suppliers/${editingSupplier.id}`)

            // Fetch suppliers
            await fetchSuppliers()

            // Close the modal
            setModalOpened(false)
        } finally {
            setDeletingSupplier(false)
        }
    }

    // Save supplier
    const [isSavingSupplier, setSavingSupplier] = useState(false)

    const saveSupplier = async () => {
        setSavingSupplier(true)

        try {
            const { data } = await requestPatch(
                `/suppliers/${editingSupplier.id}`,
                { name: editingSupplierName }
            )

            // Fetch suppliers
            await fetchSuppliers()

            // Close the modal
            setModalOpened(false)

            onChange(data)
            setSearch(data.name)
        } finally {
            setSavingSupplier(false)
        }
    }

    // Combobox
    const combobox = useCombobox({
        onDropdownClose: () => combobox.resetSelectedOption(),
    })

    const [search, setSearch] = useState('')

    const exactOptionMatch = suppliers.some(
        (supplier) =>
            supplier.name.toLowerCase() === search.trim().toLowerCase()
    )

    const filteredOptions = exactOptionMatch
        ? suppliers
        : suppliers.filter((item) =>
              item.name.toLowerCase().includes(search.toLowerCase().trim())
          )

    const options = filteredOptions.map((item) => (
        <div
            key={item.id}
            className="grid items-center gap-2 grid-cols-[1fr_30px]"
        >
            <Combobox.Option value={item} key={item.id}>
                {item.name}
            </Combobox.Option>

            <Tooltip label="Modifier ou effacer le fournisseur">
                <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => openSupplierEditor(item)}
                >
                    <IconSettings size={18} />
                </ActionIcon>
            </Tooltip>
        </div>
    ))

    // Add supplier
    const addSupplier = async () => {
        const { data } = await requestPost('/suppliers', { name: search })

        // Fetch suppliers
        await fetchSuppliers()

        onChange(data)

        // Close the modal
        setModalOpened(false)
    }

    return (
        <>
            <Modal
                title="Modifier le fournisseur"
                opened={isModalOpened}
                onClose={() => setModalOpened(false)}
            >
                <div className="flex flex-col gap-3">
                    <InputBase
                        label="Nom du fournisseur"
                        value={editingSupplierName}
                        onChange={(e) => setEditingSupplierName(e.target.value)}
                    />

                    <div className="grid grid-cols-2 gap-3">
                        <Tooltip
                            label={
                                canEditingSupplierBeDeleted
                                    ? 'Suppression immédiate'
                                    : 'Le fournisseur ne peut pas être supprimé car il est associé à des bons de commande'
                            }
                        >
                            <Button
                                disabled={!canEditingSupplierBeDeleted}
                                color="red"
                                isLoading={isDeletingSupplier}
                                onClick={deleteSupplier}
                            >
                                Effacer
                            </Button>
                        </Tooltip>

                        <Button
                            isLoading={isSavingSupplier}
                            onClick={saveSupplier}
                        >
                            Sauvegarder
                        </Button>
                    </div>
                </div>
            </Modal>

            <Combobox
                store={combobox}
                withinPortal={false}
                onOptionSubmit={(val) => {
                    if (val === '$create') {
                        addSupplier()
                    } else {
                        onChange(val)
                        setSearch(val.name)
                    }

                    combobox.closeDropdown()
                }}
            >
                <Combobox.Target>
                    <InputBase
                        label="Fournisseur"
                        rightSection={<Combobox.Chevron />}
                        value={search}
                        error={error}
                        onChange={(event) => {
                            combobox.openDropdown()
                            combobox.updateSelectedOptionIndex()
                            setSearch(event.currentTarget.value)
                        }}
                        onClick={() => combobox.openDropdown()}
                        onFocus={() => combobox.openDropdown()}
                        onBlur={() => {
                            combobox.closeDropdown()
                            setSearch(value ? value.name : '')
                        }}
                        placeholder="Associer un fournisseur"
                        rightSectionPointerEvents="none"
                    />
                </Combobox.Target>

                <Combobox.Dropdown>
                    <Combobox.Options
                        style={{ maxHeight: '200px', overflowY: 'auto' }}
                    >
                        {options}

                        {!exactOptionMatch && search.trim().length > 0 && (
                            <Combobox.Option value="$create">
                                + Ajouter le fournisseur {search}
                            </Combobox.Option>
                        )}
                    </Combobox.Options>
                </Combobox.Dropdown>
            </Combobox>
        </>
    )
}
